// Data hooks for the Maintenance module — asset list + assignee pickers.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useActiveTenant } from '@/lib/useActiveTenant'
import { withTimeout } from '@/lib/saveTimeout'
import { requireWritten } from '@/lib/requireWritten'
import type { Asset, PmSchedule, ProcedureTemplate, SpareBalance, SupplierRequest } from './types'

export function useAssets() {
  const { data: t } = useActiveTenant()
  const tenantId = t?.tenantId ?? null
  return useQuery({
    queryKey: ['maintenance-assets', tenantId],
    enabled: !!tenantId,
    queryFn: async () => {
      const { data, error } = await supabase.from('maintenance_assets').select('*')
        .eq('tenant_id', tenantId).is('deleted_at', null)
        .order('name').range(0, 4999)
      if (error) throw error
      return (data ?? []) as Asset[]
    },
    staleTime: 60_000,
  })
}

// Preventive-maintenance schedules in the active tenant (mig 757).
export function usePmSchedules() {
  const { data: t } = useActiveTenant()
  const tenantId = t?.tenantId ?? null
  return useQuery({
    queryKey: ['maintenance-pm-schedules', tenantId],
    enabled: !!tenantId,
    queryFn: async () => {
      const { data, error } = await supabase.from('maintenance_pm_schedules').select('*')
        .eq('tenant_id', tenantId).is('deleted_at', null)
        .order('name').range(0, 4999)
      if (error) throw error
      return (data ?? []) as PmSchedule[]
    },
    staleTime: 60_000,
  })
}

// Spare-parts balances in the active tenant (mig 763).
export function useSpareBalances() {
  const { data: t } = useActiveTenant()
  const tenantId = t?.tenantId ?? null
  return useQuery({
    queryKey: ['maintenance-spare-balances', tenantId],
    enabled: !!tenantId,
    queryFn: async () => {
      const { data, error } = await supabase.from('maintenance_spare_balances').select('*')
        .eq('tenant_id', tenantId).order('name').range(0, 4999)
      if (error) throw error
      return (data ?? []) as SpareBalance[]
    },
    staleTime: 30_000,
  })
}

// Supplier requests this packhouse has raised (customer side, mig 764).
export function useSupplierRequests() {
  const { data: t } = useActiveTenant()
  const tenantId = t?.tenantId ?? null
  return useQuery({
    queryKey: ['maintenance-supplier-requests', tenantId],
    enabled: !!tenantId,
    queryFn: async () => {
      const { data, error } = await supabase.from('maintenance_supplier_requests').select('*')
        .eq('customer_tenant_id', tenantId).is('deleted_at', null)
        .order('issued_at', { ascending: false }).range(0, 4999)
      if (error) throw error
      return (data ?? []) as SupplierRequest[]
    },
    staleTime: 30_000,
  })
}

// Procedure templates in the active tenant (mig 759).
export function useProcedureTemplates() {
  const { data: t } = useActiveTenant()
  const tenantId = t?.tenantId ?? null
  return useQuery({
    queryKey: ['maintenance-procedure-templates', tenantId],
    enabled: !!tenantId,
    queryFn: async () => {
      const { data, error } = await supabase.from('maintenance_procedure_templates').select('*')
        .eq('tenant_id', tenantId).is('deleted_at', null)
        .order('name').range(0, 4999)
      if (error) throw error
      return (data ?? []) as ProcedureTemplate[]
    },
    staleTime: 60_000,
  })
}

// App users in the active tenant (work-order assignees who get notifications).
export function useTenantUsers() {
  const { data: t } = useActiveTenant()
  const tenantId = t?.tenantId ?? null
  return useQuery({
    queryKey: ['maintenance-tenant-users', tenantId],
    enabled: !!tenantId,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data, error } = await supabase.from('user_tenants')
        .select('user_id, users:user_id(name)')
        .eq('tenant_id', tenantId).is('revoked_at', null)
      if (error) throw error
      return (data ?? []).map((r: any) => ({
        user_id: r.user_id as string,
        name: (Array.isArray(r.users) ? r.users[0]?.name : r.users?.name) ?? '(user)',
      })) as { user_id: string; name: string }[]
    },
  })
}

// Labour workers (clock-only assignees — no login, no notification).
// Tenant-scoped + tenant-keyed: platform admins bypass RLS, and a tenant switch
// must not serve the previous tenant's cached roster.
export function useMaintenanceWorkers() {
  const { data: t } = useActiveTenant()
  const tenantId = t?.tenantId ?? null
  return useQuery({
    queryKey: ['maintenance-workers', tenantId],
    enabled: !!tenantId,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data, error } = await supabase.from('labour_workers')
        .select('worker_number, name').eq('tenant_id', tenantId)
        .is('deleted_at', null).eq('is_active', true)
        .order('name').range(0, 4999)
      if (error) throw error
      return (data ?? []) as { worker_number: string; name: string }[]
    },
  })
}

// Resume the line: end the open downtime_log stop. ONE write path for the
// Downtime tab and Home's line-down pill (F16 UF-13), so both end a stop the
// same way and refresh every place that shows it.
export function useResumeLine(opts: { onError?: (e: Error) => void; onSuccess?: () => void } = {}) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const { data: { session } } = await supabase.auth.getSession(); const user = session?.user
      if (!user) throw new Error('Not authenticated')
      requireWritten(
        await withTimeout(supabase.from('downtime_log').update({ ended_at: new Date().toISOString(), ended_by: user.id })
          .eq('downtime_id', id).is('ended_at', null).select('downtime_id'), 'DowntimeTab: downtime_log update'),
        'This stop was already ended, or you cannot end it. Refresh the page to see the current state.',
      )
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['downtime-open'] })
      qc.invalidateQueries({ queryKey: ['downtime-today'] })
      qc.invalidateQueries({ queryKey: ['dashboard-open-downtime'] })
      opts.onSuccess?.()
    },
    onError: (e: Error) => opts.onError?.(e),
  })
}
